import React, { useState, useEffect, useRef, useCallback } from 'react';
import toast from 'react-hot-toast';
import { authorizedUsersAPI, departmentsAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import './AuthorizedUsersPage.css';

const ROLE_LABELS = {
  user:         { label: 'User',         badge: 'badge-primary', desc: 'Submit feedback only' },
  admin_editor: { label: 'Admin Editor', badge: 'badge-accent',  desc: 'Full admin access' },
  admin_viewer: { label: 'Admin Viewer', badge: 'badge-sky',     desc: 'View analytics only' },
  aboutu:       { label: 'Dept Viewer',  badge: 'badge-success', desc: 'Own dept dashboard' },
};

export default function AuthorizedUsersPage() {
  const { isEditor } = useAuth();
  const [users, setUsers]           = useState([]);
  const [total, setTotal]           = useState(0);
  const [page, setPage]             = useState(1);
  const [search, setSearch]         = useState('');
  const [loading, setLoading]       = useState(true);
  const [uploading, setUploading]   = useState(false);
  const [exporting, setExporting]   = useState(false);
  const [departments, setDepartments] = useState([]);
  const [preview, setPreview]       = useState(null);
  const [uploadMode, setUploadMode] = useState('merge');
  const [importResult, setImportResult] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editTarget, setEditTarget]     = useState(null);   // user being edited
  const [editForm, setEditForm]         = useState({ role: 'user', department_id: '' });
  const fileRef = useRef(null);
  const limit   = 20;

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, deptRes] = await Promise.all([
        authorizedUsersAPI.getAll({ page, limit, search }),
        departmentsAPI.getAll(),
      ]);
      setUsers(usersRes.data.users);
      setTotal(usersRes.data.total);
      setDepartments(deptRes.data.departments);
    } catch {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  // ── CSV file parsing ───────────────────────────────────────────────────────
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.endsWith('.csv')) { toast.error('Please upload a .csv file'); return; }

    const reader = new FileReader();
    reader.onload = (evt) => {
      const lines  = evt.target.result.split('\n').map((l) => l.trim()).filter(Boolean);
      if (lines.length < 2) { toast.error('CSV has no data rows'); return; }

      const headers  = lines[0].toLowerCase().replace(/"/g, '').split(',').map((h) => h.trim());
      const psrnIdx  = headers.findIndex((h) => h.includes('psrn') || h === 'id');
      const emailIdx = headers.findIndex((h) => h.includes('email'));
      const nameIdx  = headers.findIndex((h) => h.includes('name'));
      const roleIdx  = headers.findIndex((h) => h.includes('role'));
      const deptIdx  = headers.findIndex((h) => h.includes('department') || h.includes('dept'));

      if (psrnIdx === -1 || emailIdx === -1) {
        toast.error('CSV must have "psrn" and "email" columns'); return;
      }

      const rows = []; const errors = [];
      lines.slice(1).forEach((line, i) => {
        const cols  = parseCSVLine(line);
        const psrn  = cols[psrnIdx]?.replace(/"/g, '').trim().toUpperCase();
        const email = cols[emailIdx]?.replace(/"/g, '').trim().toLowerCase();
        const name  = nameIdx  >= 0 ? cols[nameIdx]?.replace(/"/g, '').trim()  : '';
        const role  = roleIdx  >= 0 ? cols[roleIdx]?.replace(/"/g, '').trim().toLowerCase()  : 'user';
        const dept  = deptIdx  >= 0 ? cols[deptIdx]?.replace(/"/g, '').trim()  : '';

        if (!psrn || !email) { errors.push(`Row ${i + 2}: missing psrn or email`); return; }
        rows.push({ psrn, email, name, role: role || 'user', department: dept });
      });

      if (rows.length === 0) { toast.error('No valid rows found'); return; }
      setPreview({ rows, errors, filename: file.name });
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleConfirmUpload = async () => {
    if (!preview?.rows?.length) return;
    setUploading(true);
    try {
      const res = await authorizedUsersAPI.upload(preview.rows, uploadMode);
      setImportResult({ ...res.data, mode: uploadMode });
      setPreview(null);
      toast.success(res.data.message);
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  // ── Edit role/department ───────────────────────────────────────────────────
  const openEdit = (user) => {
    setEditTarget(user);
    setEditForm({
      role:          user.role          || 'user',
      department_id: user.department_id || '',
    });
  };

  const handleSaveEdit = async () => {
    if (editForm.role === 'aboutu' && !editForm.department_id) {
      toast.error('Dept Viewer role requires a department'); return;
    }
    try {
      const res = await authorizedUsersAPI.update(editTarget.id, {
        role:          editForm.role,
        department_id: editForm.department_id || null,
      });
      setUsers((prev) => prev.map((u) =>
        u.id === editTarget.id ? { ...u, ...res.data.user } : u
      ));
      toast.success(`${editTarget.email} role updated to ${ROLE_LABELS[editForm.role]?.label}`);
      setEditTarget(null);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Update failed');
    }
  };

  // ── Toggle active ──────────────────────────────────────────────────────────
  const handleToggle = async (id, email) => {
    try {
      const res = await authorizedUsersAPI.toggle(id);
      setUsers((prev) => prev.map((u) =>
        u.id === id ? { ...u, is_active: res.data.user.is_active } : u
      ));
      toast.success(`${email} ${res.data.user.is_active ? '✅ activated' : '🔒 deactivated'}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update');
      loadUsers();
    }
  };

  // ── Delete ─────────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await authorizedUsersAPI.delete(deleteTarget.id);
      setUsers((prev) => prev.filter((u) => u.id !== deleteTarget.id));
      setTotal((t) => t - 1);
      toast.success(`${deleteTarget.email} removed`);
    } catch { toast.error('Failed to delete'); }
    finally   { setDeleteTarget(null); }
  };

  // ── Export ─────────────────────────────────────────────────────────────────
  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await authorizedUsersAPI.export();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a   = document.createElement('a');
      a.href = url; a.download = 'authorized_users.csv'; a.click();
      toast.success('Exported');
    } catch { toast.error('Export failed'); }
    finally   { setExporting(false); }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="page-container" style={{ paddingTop: 32, paddingBottom: 48 }}>

      {/* Header */}
      <div className="page-header flex-between" style={{ flexWrap: 'wrap', gap: 16 }}>
        <div>
          <h1>Authorized Users</h1>
          <p className="text-muted">
            Pre-assign roles before first login. Roles are applied automatically on sign-in.
            <span className="badge badge-success" style={{ marginLeft: 8 }}>{total} users</span>
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          {isEditor && (
            <>
              <button className="btn btn-primary" onClick={() => fileRef.current?.click()}>
                ⬆ Upload CSV
              </button>
              <input ref={fileRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={handleFileChange} />
            </>
          )}
          <button className="btn btn-ghost" onClick={handleExport} disabled={exporting}>
            {exporting ? 'Exporting…' : '⬇ Export CSV'}
          </button>
        </div>
      </div>

      {/* CSV format guide */}
      <div className="au-format-guide card">
        <div className="card-body">
          <div className="au-guide-grid">
            <div>
              <strong>📄 CSV Format</strong>
              <code className="au-format-code">psrn, email, name, role, department</code>
              <pre className="au-pre">{`psrn,email,name,role,department
2021A7PS001H,john@hyderabad.bits-pilani.ac.in,John Doe,user,
DEPT-ENG,eng@hyderabad.bits-pilani.ac.in,Engineering Dept,aboutu,Engineering
ADM001,admin@hyderabad.bits-pilani.ac.in,Admin User,admin_editor,`}</pre>
              <p className="text-small text-muted" style={{ marginTop: 6 }}>
                Valid roles: <code>user</code> · <code>admin_editor</code> · <code>admin_viewer</code> · <code>aboutu</code><br/>
                <code>department</code> column required only for <code>aboutu</code> role. Leave blank for others.
              </p>
            </div>
            <div className="au-yearly-guide">
              <strong>📅 How roles work</strong>
              <div className="au-mode-cards">
                <div className="au-mode-info au-mode-info--merge">
                  <span className="au-mode-tag">user</span>
                  <p>Can submit feedback only. Default role.</p>
                </div>
                <div className="au-mode-info au-mode-info--merge">
                  <span className="au-mode-tag">admin_editor</span>
                  <p>Full admin — manage departments, users, view analytics.</p>
                </div>
                <div className="au-mode-info au-mode-info--merge">
                  <span className="au-mode-tag">admin_viewer</span>
                  <p>Read-only analytics and audit logs.</p>
                </div>
                <div className="au-mode-info au-mode-info--replace">
                  <span className="au-mode-tag au-mode-tag--replace">aboutu</span>
                  <p>Department dashboard only — sees their own dept data. Requires a department.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Import result */}
      {importResult && (
        <div className="au-import-result animate-fade">
          <span>✅</span>
          <div>
            <strong>{importResult.message}</strong>
            <div className="au-import-stats">
              <span className="au-istat au-istat--green">+{importResult.inserted} added</span>
              <span className="au-istat au-istat--blue">{importResult.updated} updated</span>
              {importResult.deactivated > 0 && (
                <span className="au-istat au-istat--red">{importResult.deactivated} deactivated</span>
              )}
            </div>
            {importResult.errors?.length > 0 && (
              <details style={{ marginTop: 6 }}>
                <summary className="text-small text-muted" style={{ cursor: 'pointer' }}>
                  {importResult.errors.length} rows skipped
                </summary>
                <ul style={{ marginTop: 4, paddingLeft: 16 }}>
                  {importResult.errors.map((e, i) => <li key={i} className="text-small text-muted">{e}</li>)}
                </ul>
              </details>
            )}
          </div>
          <button className="au-close-btn" onClick={() => setImportResult(null)}>×</button>
        </div>
      )}

      {/* Search */}
      <div className="au-search-bar">
        <input
          className="form-input"
          placeholder="Search PSRN, email or name…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
        />
        {search && <button className="btn btn-ghost btn-sm" onClick={() => setSearch('')}>Clear</button>}
      </div>

      {/* Table */}
      <div className="card animate-fade">
        {loading ? (
          <div className="flex-center" style={{ height: 200 }}><div className="spinner" /></div>
        ) : (
          <>
            <div className="au-table-wrap">
              <table className="au-table">
                <thead>
                  <tr>
                    <th>PSRN</th>
                    <th>Email</th>
                    <th>Name</th>
                    <th>Role</th>
                    <th>Department</th>
                    <th>Status</th>
                    <th>Added</th>
                    {isEditor && <th style={{ textAlign: 'right' }}>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className={!u.is_active ? 'au-row--inactive' : ''}>
                      <td><code className="au-psrn">{u.psrn}</code></td>
                      <td>{u.email}</td>
                      <td className="text-muted">{u.name || '—'}</td>
                      <td>
                        <span className={`badge ${ROLE_LABELS[u.role]?.badge || 'badge-primary'}`}>
                          {ROLE_LABELS[u.role]?.label || u.role}
                        </span>
                      </td>
                      <td className="text-muted text-small">
                        {u.department_name || '—'}
                      </td>
                      <td>
                        <span className={`badge ${u.is_active ? 'badge-success' : 'badge-danger'}`}>
                          {u.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="text-muted text-small">
                        {new Date(u.created_at).toLocaleDateString()}
                      </td>
                      {isEditor && (
                        <td>
                          <div className="au-actions">
                            <button className="btn btn-ghost btn-sm" onClick={() => openEdit(u)}>
                              Edit Role
                            </button>
                            <button
                              className={`btn btn-sm ${u.is_active ? 'btn-ghost' : 'btn-outline'}`}
                              onClick={() => handleToggle(u.id, u.email)}
                            >
                              {u.is_active ? 'Deactivate' : 'Activate'}
                            </button>
                            <button className="btn btn-sm btn-danger" onClick={() => setDeleteTarget(u)}>
                              Remove
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                  {users.length === 0 && (
                    <tr>
                      <td colSpan={isEditor ? 8 : 7} className="au-empty">
                        {search ? `No users matching "${search}"` : 'No authorized users yet. Upload a CSV to get started.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="au-pagination">
                <span className="text-small text-muted">{total} users</span>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>← Prev</button>
                  <span className="text-small">Page {page} of {totalPages}</span>
                  <button className="btn btn-ghost btn-sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Next →</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Edit Role Modal ── */}
      {editTarget && (
        <div className="modal-overlay" onClick={() => setEditTarget(null)}>
          <div className="modal animate-scale" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Role — {editTarget.email}</h3>
              <button className="modal-close" onClick={() => setEditTarget(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Role</label>
                <select
                  className="form-select"
                  value={editForm.role}
                  onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                >
                  {Object.entries(ROLE_LABELS).map(([val, info]) => (
                    <option key={val} value={val}>{info.label} — {info.desc}</option>
                  ))}
                </select>
              </div>

              {editForm.role === 'aboutu' && (
                <div className="form-group mt-4">
                  <label className="form-label">Department <span style={{ color: 'var(--red)' }}>*</span></label>
                  <select
                    className="form-select"
                    value={editForm.department_id}
                    onChange={(e) => setEditForm({ ...editForm, department_id: e.target.value })}
                  >
                    <option value="">Select department…</option>
                    {departments.map((d) => (
                      <option key={d.id} value={d.id}>{d.name}</option>
                    ))}
                  </select>
                  <p className="text-small text-muted mt-2">
                    Required for Dept Viewer role — they will only see this department's data.
                  </p>
                </div>
              )}

              {editForm.role !== 'aboutu' && editForm.department_id && (
                <p className="text-small text-muted mt-2">
                  ℹ Department will be cleared when saving a non-aboutu role.
                </p>
              )}

              <div className="au-role-info mt-4">
                <span className={`badge ${ROLE_LABELS[editForm.role]?.badge}`}>
                  {ROLE_LABELS[editForm.role]?.label}
                </span>
                <span className="text-small text-muted" style={{ marginLeft: 8 }}>
                  {ROLE_LABELS[editForm.role]?.desc}
                </span>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setEditTarget(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSaveEdit}>Save Changes</button>
            </div>
          </div>
        </div>
      )}

      {/* ── CSV Preview Modal ── */}
      {preview && (
        <div className="modal-overlay" onClick={() => setPreview(null)}>
          <div className="modal au-preview-modal animate-scale" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>📋 Preview — {preview.filename}</h3>
              <button className="modal-close" onClick={() => setPreview(null)}>×</button>
            </div>
            <div className="modal-body" style={{ padding: 0 }}>
              <div className="au-preview-stats">
                <div className="au-stat au-stat--good">
                  <span className="au-stat-num">{preview.rows.length}</span>
                  <span className="au-stat-label">Valid rows</span>
                </div>
                <div className={`au-stat ${preview.errors.length > 0 ? 'au-stat--warn' : 'au-stat--good'}`}>
                  <span className="au-stat-num">{preview.errors.length}</span>
                  <span className="au-stat-label">Skipped</span>
                </div>
              </div>

              {/* Mode selector */}
              <div className="au-mode-selector">
                <p className="text-small" style={{ fontWeight: 600, marginBottom: 10 }}>Choose import mode:</p>
                <div className="au-mode-btns">
                  <button className={`au-mode-btn ${uploadMode === 'merge' ? 'au-mode-btn--active' : ''}`} onClick={() => setUploadMode('merge')}>
                    <strong>Merge</strong>
                    <span>Add new + update existing. Others untouched.</span>
                  </button>
                  <button className={`au-mode-btn au-mode-btn--replace ${uploadMode === 'replace' ? 'au-mode-btn--active-replace' : ''}`} onClick={() => setUploadMode('replace')}>
                    <strong>Full Replace (Year Reset)</strong>
                    <span>Add new, update existing, <strong>deactivate everyone not in this list</strong>.</span>
                  </button>
                </div>
                {uploadMode === 'replace' && (
                  <div className="au-replace-warning">
                    ⚠ Full Replace will deactivate all users not in this CSV. Use at start of each year.
                  </div>
                )}
              </div>

              <div className="au-table-wrap" style={{ maxHeight: 260, overflowY: 'auto' }}>
                <table className="au-table">
                  <thead><tr><th>#</th><th>PSRN</th><th>Email</th><th>Name</th><th>Role</th><th>Dept</th></tr></thead>
                  <tbody>
                    {preview.rows.slice(0, 50).map((r, i) => (
                      <tr key={i}>
                        <td className="text-muted text-small">{i + 1}</td>
                        <td><code className="au-psrn">{r.psrn}</code></td>
                        <td>{r.email}</td>
                        <td className="text-muted">{r.name || '—'}</td>
                        <td>
                          <span className={`badge ${ROLE_LABELS[r.role]?.badge || 'badge-primary'}`} style={{ fontSize: '0.7rem' }}>
                            {ROLE_LABELS[r.role]?.label || r.role}
                          </span>
                        </td>
                        <td className="text-muted text-small">{r.department || '—'}</td>
                      </tr>
                    ))}
                    {preview.rows.length > 50 && (
                      <tr><td colSpan={6} style={{ textAlign: 'center', padding: 10, color: 'var(--text-muted)' }}>
                        … and {preview.rows.length - 50} more rows
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setPreview(null)}>Cancel</button>
              <button
                className={`btn ${uploadMode === 'replace' ? 'btn-danger' : 'btn-primary'}`}
                onClick={handleConfirmUpload}
                disabled={uploading}
              >
                {uploading ? 'Importing…' : `Confirm ${uploadMode === 'replace' ? 'Full Replace' : 'Merge'} (${preview.rows.length} users)`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteTarget && (
        <div className="modal-overlay" onClick={() => setDeleteTarget(null)}>
          <div className="modal animate-scale" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Remove from Whitelist</h3>
              <button className="modal-close" onClick={() => setDeleteTarget(null)}>×</button>
            </div>
            <div className="modal-body">
              <p>Remove <strong>{deleteTarget.email}</strong> ({deleteTarget.psrn})?</p>
              <p className="text-muted text-small mt-2">They will no longer be able to log in.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={handleDelete}>Remove</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

function parseCSVLine(line) {
  const result = []; let current = ''; let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') { inQuotes = !inQuotes; }
    else if (ch === ',' && !inQuotes) { result.push(current); current = ''; }
    else { current += ch; }
  }
  result.push(current);
  return result;
}
